import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { generateBufferId } from "@repo/structure";
import { useFeatureGuardian } from "@hooks";
import { LOCKED_FEATURES } from "@utils";
import { useEstimateStructure } from "@tools";
import { DEFAULT_PERCENT } from "@tools/EstimateEditor/utils";
import { Button, Dialog, SortableListV2, TooltipIcon } from "@components";
import Grid from "@material-ui/core/Grid";
import { Badge, IconButton } from "@material-ui/core";
import { AddBufferButton, BufferEntry, NewBuffer, BufferDisplaySelect } from "./components";
import { Stacks } from "@assets";

export const ProjectBuffers = observer(() => {
  const structure = useEstimateStructure();
  const { t } = useTranslation();
  
  const [ visible, show ] = useState(false);
  const [ tmpBuffer, setTmpBuffer ] = useState(null);
  
  const { checkAccess, FeatureAlert } = useFeatureGuardian(
    LOCKED_FEATURES.BUFFERS
  );
  
  const displayBuffers = structure.settings.buffers || [];
  const usedBuffers = displayBuffers.filter(ov => ov.used)?.length;
  const availableWorkTypes = structure.workTypes;
  
  const dialogTitle = useMemo(() => (
    t("views.editor.dialogs.buffers.title.project")
  ), []);
  
  const handleOpen = async () => {
    const allowed = await checkAccess();
    if (!allowed) return;
    show(true);
  }
  
  const handleClose = () => {
    show(false);
    setTmpBuffer(null);
  }
  
  const addNewBuffer = () => {
    structure.settings.addBuffer(tmpBuffer);
    setTmpBuffer(null);
  }
  
  const addTmpBuffer = async () => {
    const name = t("views.editor.dialogs.buffers.name_new");
    const oldNameUses = displayBuffers.filter(ov => (
      ov.name.includes(name)
    )).length;
    
    setTmpBuffer({
      id: generateBufferId(),
      name: oldNameUses > 0 ? `${name} (${oldNameUses+1})` : name,
      percent: DEFAULT_PERCENT,
      workTypes: [],
      used: true,
    });
  }
  
  const handleDisplayBuffersReorder = (list) => {
    structure.settings.reorderBuffers(list);
  };
  
  const handleTmpBufferChange = (updatedData) => {
    setTmpBuffer({
      ...tmpBuffer,
      ...updatedData
    })
  }
  
  return (
    <>
      <Badge
        badgeContent={usedBuffers}
        overlap="circular"
        color="primary"
        aria-label="number of used buffers"
        max={9}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        classes={{
          badge: "r-1"
        }}
      >
        <IconButton
          id="projectBuffers"
          onClick={handleOpen}
          aria-label={dialogTitle}
          color="primary"
          className="text-xl"
        >
          <Stacks color="primary" />
        </IconButton>
      </Badge>
      {
        visible &&
        <Dialog
          open
          onClose={handleClose}
          width={624}
          title={<Grid container alignItems="center">
            <h2>{dialogTitle}</h2>
            <TooltipIcon className="ml-2" color="primary">
              {t("views.editor.dialogs.buffers.info.project")}
            </TooltipIcon>
          </Grid>}
          actions={
            <Button
              variant="contained"
              color="secondary"
              onClick={handleClose}
            >
              {t("common.close")}
            </Button>
          }
        >
          <Grid item container direction="column">
            <SortableListV2
              list={displayBuffers}
              group="displayBuffers"
              onListReorder={handleDisplayBuffersReorder}
              useDragHandle lockAxis
              itemContainerProps={{ alignItems: "flex-start" }}
              dragHandleProps={{ style: { marginTop: 14 } }}
              display="flex"
              direction="column"
            >
              {
                displayBuffers.map((buffer) => (
                  <BufferEntry
                    buffer={buffer}
                    key={buffer.id}
                    id={buffer.id}
                    availableWorkTypes={availableWorkTypes}
                  />
                ))
              }
            </SortableListV2>
            {/*{*/}
            {/*  Boolean(displayBuffers.length) &&*/}
            {/*  <Divider className="my-3" />*/}
            {/*}*/}
            {
              tmpBuffer
                ? <NewBuffer
                  onChange={handleTmpBufferChange}
                  onAccept={addNewBuffer}
                  onCancel={() => setTmpBuffer(null)}
                  availableWorkTypes={availableWorkTypes}
                  {...tmpBuffer}
                />
                : <AddBufferButton onAdd={addTmpBuffer} />
            }
            
            <BufferDisplaySelect />
          </Grid>
        </Dialog>
      }
      <FeatureAlert />
    </>
  );
});